// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// =============================================================================
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * URL affichable du logo d'une licence, ou null.
 *
 * ⚠️ POURQUOI PAS UN SIMPLE `<img src=".../api/enterprise/logo/...">` : la route
 * exige un jeton, et une balise `<img>` ne peut pas envoyer d'en-tête
 * `Authorization`. La barre latérale le faisait pourtant — le serveur répondait
 * 401, `onError` masquait l'image, et AUCUN logo de licence ne s'y est jamais
 * affiché (constaté le 15/09/2026).
 *
 * L'autre contournement, `?token=` dans l'URL, écrivait la session dans les
 * journaux du serveur, l'historique et les en-têtes `Referer` — le serveur le
 * refuse depuis le 21/09/2026 (cf `require_auth_download`). Le logo est donc
 * chargé en `fetch` avec l'en-tête, puis affiché depuis une URL `data:`.
 *
 * ⚠️ `data:` ET NON `blob:`. La première version passait par
 * `URL.createObjectURL` : parfait en développement, IMAGE CASSÉE en production.
 * La politique de sécurité du nginx du VPS n'autorise, pour les images, que
 * `'self' data: https:` — une URL `blob:` y est refusée par le navigateur après
 * un téléchargement pourtant réussi (constaté le 17/09/2026). `data:` y figure
 * déjà : pas de configuration serveur à retoucher, et le logo normalisé
 * (PNG ≤ 480×160, `core/logos_licence.py`) pèse quelques dizaines de Ko.
 *
 * Le nom de fichier change à chaque téléversement (`core/logos_licence.py`) :
 * suivre `logoPath` suffit à recharger un logo remplacé.
 */
export function useLogoLicence(logoPath) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!logoPath) { setUrl(null); return undefined; }
    let annule = false;

    // La session part en cookie (chantier ⑯, lib/session.js).
    fetch(`${API}/api/enterprise/logo/${encodeURIComponent(logoPath)}`)
      .then(r => (r.ok ? r.blob() : null))
      .then(blob => new Promise((resoudre) => {
        if (!blob) { resoudre(null); return; }
        const lecteur = new FileReader();
        lecteur.onload = () => resoudre(lecteur.result);
        lecteur.onerror = () => resoudre(null);
        lecteur.readAsDataURL(blob);
      }))
      .then(donnees => { if (!annule) setUrl(donnees); })
      .catch(() => { if (!annule) setUrl(null); });

    return () => { annule = true; };
  }, [logoPath]);

  return url;
}
